import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import mentorService from '../../../../services/mentorService';
import courseService from '../../../../services/mentor/courseService';
import enrollmentService from '../../../../services/enrollmentService';
import batchService from '../../../../services/mentor/batchService';
import { updateUserStatus, deleteUserAccount } from '../../../../services/mentor/userService';
import type { User } from '../../../../types/user';
import type { ICourse } from '../../../../types/course';
import type { IBatch } from '../../../../types/batch'; // ✅ Correctly imported from shared types
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Plus, Check, X, UserX, UserCheck, UserCog, Search, Users } from 'lucide-react';

// Lazy load the Modal component
const Modal = lazy(() => import('../../../../components/common/Modal'));

// ❌ The duplicate IBatch interface has been removed from here

const StudentsList: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [allStudents, setAllStudents] = useState<User[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<User[]>([]);
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [batches, setBatches] = useState<IBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [selectedStudentForEnroll, setSelectedStudentForEnroll] = useState<User | null>(null);
  const [selectedCourseForEnroll, setSelectedCourseForEnroll] = useState('');
  const [selectedBatchForEnroll, setSelectedBatchForEnroll] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [studentsData, coursesData] = await Promise.all([
        mentorService.getAllStudents(),
        courseService.getAllCoursesForAdmin(),
      ]);
      setAllStudents(studentsData);
      setFilteredStudents(studentsData);
      setCourses(coursesData);
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchBatchesForSelectedCourse = async () => {
      setBatches([]);
      setSelectedBatchForEnroll(null);
      if (selectedCourseForEnroll) {
        try {
          // ✅ Corrected function name to match your service file
          const fetchedBatches = await batchService.getBatchesByCourse(selectedCourseForEnroll);
          setBatches(fetchedBatches);
        } catch (err: any) {
          showToast(`Failed to load batches: ${err.message}`, { type: 'error' });
          setBatches([]);
        }
      }
    };
    fetchBatchesForSelectedCourse();
  }, [selectedCourseForEnroll]);

  useEffect(() => {
    if (!isAuthLoading && user) {
      fetchAllData();
    }
  }, [isAuthLoading, user]);
  
  useEffect(() => {
    const lowercasedFilter = searchTerm.toLowerCase();
    const filteredData = allStudents.filter(student =>
        student.name.toLowerCase().includes(lowercasedFilter) ||
        student.email.toLowerCase().includes(lowercasedFilter)
    );
    setFilteredStudents(filteredData);
  }, [searchTerm, allStudents]);

  const handleOpenEnrollModal = (student: User) => {
    setSelectedStudentForEnroll(student);
    setSelectedCourseForEnroll('');
    setSelectedBatchForEnroll(null);
    setBatches([]);
    setIsEnrollModalOpen(true);
  };

  const handleCloseEnrollModal = () => {
    setIsEnrollModalOpen(false);
    setSelectedStudentForEnroll(null);
    setSelectedCourseForEnroll('');
    setSelectedBatchForEnroll(null);
    setBatches([]);
  };

  const handleEnrollSubmit = async () => {
    if (!selectedStudentForEnroll || !selectedCourseForEnroll || !selectedBatchForEnroll) {
      return showToast('Please select a student, course, and batch.', { type: 'info' });
    }
    setIsProcessing(selectedStudentForEnroll._id);
    try {
      const response = await enrollmentService.enrollStudent({
        studentId: selectedStudentForEnroll._id,
        courseId: selectedCourseForEnroll,
        batchId: selectedBatchForEnroll,
      });
      showToast(response.message, { type: 'success' });
      handleCloseEnrollModal();
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleApproveRejectStudent = async (student: User, status: 'approve' | 'reject') => {
    const actionText = status === 'approve' ? 'approve' : 'reject and delete';
    if (!window.confirm(`Are you sure you want to ${actionText} ${student.name}?`)) return;

    setIsProcessing(student._id);
    try {
      let message;
      if (status === 'approve') {
        message = await mentorService.approveStudent(student._id);
        setAllStudents(prev => prev.map(s => s._id === student._id ? { ...s, isApproved: true } : s));
      } else {
        message = await mentorService.rejectStudent(student._id);
        setAllStudents(prev => prev.filter(s => s._id !== student._id));
      }
      showToast(message, { type: 'success' });
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleUpdateStatus = async (student: User) => {
    const newStatus = student.status === 'active' ? 'suspended' : 'active';
    const actionText = newStatus === 'suspended' ? 'suspend' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${actionText} ${student.name}'s account?`)) return;

    setIsProcessing(student._id);
    try {
      await updateUserStatus(student._id, newStatus);
      showToast(`User account has been ${newStatus}.`, { type: 'success' });
      setAllStudents(prev => prev.map(s => s._id === student._id ? { ...s, status: newStatus } : s));
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDeleteAccount = async (student: User) => {
    if (!window.confirm(`WARNING: This will PERMANENTLY delete ${student.name}'s account. Proceed?`)) return;
    setIsProcessing(student._id);
    try {
      const message = await deleteUserAccount(student._id);
      showToast(message, { type: 'success' });
      setAllStudents(prev => prev.filter(s => s._id !== student._id));
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  if (isAuthLoading || isLoading) {
    return <Loader />;
  }

  const renderActionButtons = (student: User) => (
    <div className="flex justify-end items-center gap-2">
      {!student.isApproved ? (
        <>
          <button onClick={() => handleApproveRejectStudent(student, 'approve')} disabled={!!isProcessing} className="p-2 text-green-600 hover:bg-green-100 rounded-full disabled:opacity-50" title="Approve">
            <Check className="h-5 w-5" />
          </button>
          <button onClick={() => handleApproveRejectStudent(student, 'reject')} disabled={!!isProcessing} className="p-2 text-red-600 hover:bg-red-100 rounded-full disabled:opacity-50" title="Reject">
            <X className="h-5 w-5" />
          </button>
        </>
      ) : (
        <button onClick={() => handleOpenEnrollModal(student)} disabled={!!isProcessing} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full disabled:opacity-50" title="Enroll Student">
          <Plus className="h-5 w-5" />
        </button>
      )}
      {user?.isAdmin && (
        <>
          <button onClick={() => handleUpdateStatus(student)} disabled={isProcessing === student._id} title={student.status === 'active' ? 'Suspend Account' : 'Reactivate Account'} className="p-2 text-yellow-600 hover:bg-yellow-100 rounded-full disabled:opacity-50">
            {student.status === 'active' ? <UserCog className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
          </button>
          <button onClick={() => handleDeleteAccount(student)} disabled={isProcessing === student._id} title="Delete Account" className="p-2 text-red-600 hover:bg-red-100 rounded-full disabled:opacity-50">
            <UserX className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Student Management</h1>
            <p className="text-gray-500 mt-1">Approve, enroll, and manage all student accounts.</p>
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition"
            />
          </div>
        </div>
      </header>

      {filteredStudents.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No Students Found</h3>
          <p className="mt-1 text-sm text-gray-500">{searchTerm ? 'No students match your search.' : 'There are no students to display.'}</p>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Approval Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Account Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map(student => (
                  <tr key={student._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-gray-900">{student.name}</p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${student.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {student.isApproved ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full capitalize ${student.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                        {student.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {renderActionButtons(student)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {filteredStudents.map(student => (
              <div key={student._id} className="bg-white p-4 rounded-2xl shadow-sm border space-y-4">
                <div>
                  <p className="font-bold text-gray-900">{student.name}</p>
                  <p className="text-sm text-gray-500">{student.email}</p>
                </div>
                <div className="flex items-center justify-between text-sm border-t pt-4">
                  <div>
                    <p className="text-gray-600 font-medium mb-1">Approval</p>
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${student.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {student.isApproved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-600 font-medium mb-1">Account</p>
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full capitalize ${student.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                      {student.status}
                    </span>
                  </div>
                </div>
                <div className="border-t pt-4">
                  {renderActionButtons(student)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      
      <Suspense fallback={null}>
        <Modal isOpen={isEnrollModalOpen} onClose={handleCloseEnrollModal} title={`Enroll ${selectedStudentForEnroll?.name || 'Student'}`}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Select a course and batch to enroll this student in:</p>
            <div>
              <label htmlFor="courseSelectModal" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              <select
                id="courseSelectModal"
                value={selectedCourseForEnroll}
                onChange={(e) => setSelectedCourseForEnroll(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                disabled={!!isProcessing}
              >
                <option value="">-- Choose Course --</option>
                {courses.map(course => (
                  <option key={course._id} value={course._id}>{course.title}</option>
                ))}
              </select>
            </div>
            {selectedCourseForEnroll && (
              <div>
                <label htmlFor="batchSelectModal" className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
                <select
                  id="batchSelectModal"
                  value={selectedBatchForEnroll || ''}
                  onChange={(e) => setSelectedBatchForEnroll(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  disabled={!!isProcessing || batches.length === 0}
                >
                  <option value="">-- Choose Batch --</option>
                  {batches.length === 0 ? (
                    <option value="" disabled>No batches available</option>
                  ) : (
                    batches.map(batch => (
                      <option key={batch._id} value={batch._id}>{batch.name}</option>
                    ))
                  )}
                </select>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <button onClick={handleCloseEnrollModal} className="px-4 py-2 text-sm font-medium bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50" disabled={!!isProcessing}>Cancel</button>
              <button
                onClick={handleEnrollSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={!!isProcessing || !selectedCourseForEnroll || !selectedBatchForEnroll}
              >
                {isProcessing ? 'Enrolling...' : 'Enroll Student'}
              </button>
            </div>
          </div>
        </Modal>
      </Suspense>
    </div>
  );
};

export default StudentsList;